import React from 'react'

// Animated hero illustration: the lab's digital workflow as a loop. Plain SVG + CSS keyframes
// (see the WORKFLOW banner in src/css/site/site.css) - no animation library, a few KB instead
// of the ~215 kB three.js chunk it replaced.
//
// Geometry lives in one place so the CSS timing can stay simple: a dot orbits the ring once
// per cycle, clockwise from the top, and the steps sit at equal angles starting at the top.
// Each node's highlight is the same keyframe offset by its share of the cycle, so the node
// lights up exactly when the dot arrives. CYCLE must match --nv-wf-cycle in site.css.
//
// Labels sit OUTSIDE the ring, on the side facing away from the centre (above the top node,
// right of the right node, ...). Labels placed under every node had the ring running through
// the left and right ones. The canvas is wider than tall to make room for the side labels.
const W = 680;
const H = 500;
const CX = W / 2;
const CY = H / 2;
const R = 165;
const CIRC = 2 * Math.PI * R;
const CYCLE = 12; // seconds
const NODE_R = 34;

const ICONS = {
    // Tooth with a scan beam across it.
    scan: (
        <>
            <path d="M8 3C5.8 3 4.5 4.7 4.5 7c0 3 1 4.5 1.8 7.5.5 2 .9 6.5 2.7 6.5s1.6-4 2.3-5.5c.3-.7.9-.7 1.4 0 .7 1.5.6 5.5 2.3 5.5s2.2-4.5 2.7-6.5c.8-3 1.8-4.5 1.8-7.5 0-2.3-1.3-4-3.5-4-1.7 0-2.5 1-4 1S9.7 3 8 3z" />
            <path d="M1.5 10.5h21" />
        </>
    ),
    design: (
        <>
            <rect x="3" y="4" width="18" height="12" rx="2" />
            <path d="M8 20h8M12 16v4" />
            <path d="M8.5 12c1-3 2-4 3.5-4s2.5 1 3.5 4" />
        </>
    ),
    // Milling bur: shank, fluted cutting head, chips at the tip.
    mill: (
        <>
            <path d="M9 2h6v4H9z" />
            <path d="M9 6h6l-1 9-2 4-2-4z" />
            <path d="M9.4 9.4l5.2-1.8M9.8 12.6l4.4-1.6" />
            <path d="M5.8 19.6l1.6.8M18.2 19.6l-1.6.8M8.4 22.6h.01M15.6 22.6h.01M12 23h.01" />
        </>
    ),
    deliver: (
        <>
            <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
            <path d="M3 8l9 5 9-5M12 13v8" />
        </>
    )
};

// Small twinkling points, purely decorative. Placed by hand clear of the four labels.
const SPARKS = [[130, 120], [540, 95], [580, 360], [120, 390], [440, 40], [620, 180], [80, 170]];

// Where a node's label goes, by which side of the ring the node is on.
const labelProps = (x, y) => {
    const dx = x - CX;
    const dy = y - CY;
    if (Math.abs(dx) > Math.abs(dy)) {
        return dx > 0
            ? { x: x + NODE_R + 14, y: y + 5, textAnchor: 'start' }
            : { x: x - NODE_R - 14, y: y + 5, textAnchor: 'end' };
    }
    return dy < 0
        ? { x, y: y - NODE_R - 16, textAnchor: 'middle' }
        : { x, y: y + NODE_R + 26, textAnchor: 'middle' };
};

const Workflow = ({ block }) => {
    const n = block.steps.length;
    const pos = (i) => {
        const a = (-90 + (360 / n) * i) * (Math.PI / 180);
        return [CX + R * Math.cos(a), CY + R * Math.sin(a)];
    };

    return (
        <svg className="nv-wf" viewBox={`0 0 ${W} ${H}`} role="img"
             aria-label={block.caption.join(' ') + ': ' + block.steps.map((s) => s.label).join(', ')}>
            <defs>
                <radialGradient id="nv-wf-glow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#6946C4" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#6946C4" stopOpacity="0" />
                </radialGradient>
                <linearGradient id="nv-wf-arc" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#A78BFA" />
                    <stop offset="100%" stopColor="#E9E3FF" />
                </linearGradient>
                <linearGradient id="nv-wf-tooth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" />
                    <stop offset="100%" stopColor="#C9BCF5" />
                </linearGradient>
                <filter id="nv-wf-blur" x="-100%" y="-100%" width="300%" height="300%">
                    <feGaussianBlur stdDeviation="4" />
                </filter>
            </defs>

            <circle cx={CX} cy={CY} r={R + 40} fill="url(#nv-wf-glow)" />

            {SPARKS.map(([x, y], i) => (
                <circle key={i} className="nv-wf-spark" cx={x} cy={y} r="2"
                        style={{ animationDelay: `${(i * 0.7) % 3}s` }} />
            ))}

            {/* Track, and the progress arc that fills behind the moving dot. */}
            <circle className="nv-wf-track" cx={CX} cy={CY} r={R} />
            <circle className="nv-wf-arc" cx={CX} cy={CY} r={R}
                    strokeDasharray={CIRC} strokeDashoffset={CIRC}
                    transform={`rotate(-90 ${CX} ${CY})`}
                    style={{ '--nv-wf-circ': CIRC }} />

            {/* Centre: the tooth, gently floating, with the caption under it. */}
            <g className="nv-wf-center">
                {/* Two layers on purpose: the CSS float animation sets `transform`, which
                    replaces an SVG transform attribute on the same element - so the positioning
                    translate lives on the outer group and the animation on the inner one. */}
                <g transform={`translate(${CX - 32} ${CY - 78})`}>
                    <g className="nv-wf-tooth">
                        <path d="M22 4C13 4 7 10 7 19c0 11 4 17 7 28 2 7 3 17 8 17s5-11 8-17c1-2 3-2 4 0 3 6 3 17 8 17s6-10 8-17c3-11 7-17 7-28 0-9-6-15-15-15-5 0-7 3-10 3S27 4 22 4z"
                              fill="url(#nv-wf-tooth)" />
                        <path d="M16 13c-3 2-4 5-4 9" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
                    </g>
                </g>
                <text className="nv-wf-caption" x={CX} y={CY + 30} textAnchor="middle">
                    <tspan x={CX}>{block.caption[0]}</tspan>
                    <tspan x={CX} dy="1.3em">{block.caption[1]}</tspan>
                </text>
            </g>

            {/* Steps. The negative delay puts node i's highlight at i/n of the cycle. */}
            {/* The travelling dot: a group rotated around the centre, dot parked at the top.
                transform-origin is set here from the same constants as the ring, so the two
                cannot drift apart if the geometry changes.
                Drawn BEFORE the steps on purpose: SVG paints in document order, so the opaque
                nodes cover the dot and it passes behind each checkpoint instead of over it. */}
            <g className="nv-wf-orbit" style={{ transformOrigin: `${CX}px ${CY}px` }}>
                <circle cx={CX} cy={CY - R} r="12" fill="#A78BFA" opacity="0.6" filter="url(#nv-wf-blur)" />
                <circle cx={CX} cy={CY - R} r="6" fill="#FFFFFF" />
            </g>

            {block.steps.map((s, i) => {
                const [x, y] = pos(i);
                const delay = `${(CYCLE / n) * i - CYCLE}s`;
                return (
                    <g key={s.label}>
                        <circle className="nv-wf-node" cx={x} cy={y} r={NODE_R} style={{ animationDelay: delay }} />
                        <svg x={x - 14} y={y - 14} width="28" height="28" viewBox="0 0 24 24"
                             className="nv-wf-icon" style={{ animationDelay: delay }}>
                            {ICONS[s.icon]}
                        </svg>
                        <text className="nv-wf-label" {...labelProps(x, y)}>{s.label}</text>
                    </g>
                );
            })}
        </svg>
    );
};

export default Workflow;
